// [Project] > [Status] > [All actions] - every action every specialist has written on the
// project, open and closed, in one table led by the Manager's ranking.
// See docs/Y2026-Q4/design-all-actions-tab.md.
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import { useDashboard } from "../api/hooks/useDashboard";
import { BriefingFrame } from "../components/BriefingFrame";
import { ActionTable } from "../components/NextActionTable";
import { SectionBlock, SectionCard } from "../components/SpecialistRowSections";
import { managerRanking } from "../utils/nextActions";
import { useScrollToHash } from "../utils/useScrollToHash";
import styles from "./ProjectStatusTab.module.css";

export const ProjectAllActionsTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const status = useProjectAgentStatus(decoded || null);
  // The same query as the Overview's summary row, which holds the Manager's ranking.
  const dashboard = useDashboard(decoded);
  const row = dashboard.data?.projects.find((p) => p.project_id === decoded);
  const manager = useMemo(() => managerRanking(row), [row]);
  // Once the table renders, which waits on the ranking too.
  useScrollToHash(!!status.data && !dashboard.isLoading);

  if (status.isLoading || dashboard.isLoading)
    return <p className={styles.muted}>Loading the actions…</p>;
  if (status.error)
    return (
      <p className={styles.error}>
        Could not load this project&apos;s actions.
      </p>
    );
  if (!status.data) return null;

  return (
    <BriefingFrame projectId={decoded} level="specialist_details">
      <div className={styles.tab}>
        <SectionCard title="Next actions" aside={null}>
          <SectionBlock>
            <div className={styles.tab}>
              {dashboard.error ? (
                <p className={styles.error}>
                  Could not load the Manager&apos;s ranking, so Priority starts
                  from each specialist&apos;s own ranking. Reload to try again.
                </p>
              ) : (
                <p className={styles.muted}>
                  Every action each specialist has written on this project, open
                  and closed. Priority puts the Manager&apos;s ranking first, then
                  each specialist&apos;s own.
                </p>
              )}
              <ActionTable
                scope={{ kind: "all" }}
                sources={[
                  {
                    projectId: decoded,
                    projectName: "",
                    data: status.data,
                    manager,
                  },
                ]}
                empty="No specialist has written an action on this project yet."
                controls
                showArea={false}
                anchored
              />
            </div>
          </SectionBlock>
        </SectionCard>
      </div>
    </BriefingFrame>
  );
};
